import {
	type Point,
	type Spoke,
	radarLabelLines,
	roundedPolygonMarkerPoint,
	roundedPolygonPath,
	vertex,
} from "@/lib/radar";
import { cn } from "@/lib/utils";

/**
 * The activity radar: which domains someone is learning in, and how far around
 * each one they have gone.
 *
 * DELIBERATELY WITHOUT NUMBERS. The underlying score is a tier on a ladder, and
 * a tier means nothing to a reader until we explain it — throwing "Tier 4 ·
 * 23.2" at an admin invites them to invent a meaning for it, and the one they
 * invent will be "how good this person is". The shape says where the learning
 * is; the figures wait for the explanation we owe them.
 *
 * Rings are unlabelled for the same reason, and there is always one ring beyond
 * the furthest point, so the edge never reads as a ceiling.
 */

const ORANGE = "var(--orange-primary)";
const ORANGE_FILL = "rgba(236, 119, 35, 0.16)";
const POTENTIAL_FILL = "rgba(236, 119, 35, 0.06)";
const EARLIER = "var(--muted2)";
const GRID = "var(--divider)";
const LABEL = "var(--heading)";

export interface RadarChartProps {
	spokes: Spoke[];
	rings: number;
	/** Draw the comparison outline, when the data carries one. */
	showBefore?: boolean;
	size?: number;
	className?: string;
	/** Announced to a screen reader in place of the drawing. */
	title: string;
}

export function RadarChart({
	spokes,
	rings,
	showBefore = true,
	size = 340,
	className,
	title,
}: RadarChartProps) {
	// Labels sit outside the rings, so the drawing is inset from the viewBox.
	const padding = 58;
	const radius = (size - padding * 2) / 2;
	const centre = { x: size / 2, y: size / 2 };
	const safeRings = Math.max(2, rings);
	const hasBefore = showBefore && spokes.some((spoke) => spoke.before !== null);
	const hasPotential = spokes.some(
		(spoke) => spoke.potential !== null && spoke.potential > spoke.position,
	);
	const pointsAt = (value: (spoke: Spoke) => number | null): Point[] =>
		spokes.map((spoke, index) => {
			const point = vertex(
				index,
				spokes.length,
				value(spoke) ?? 0,
				safeRings,
				radius,
			);
			return { x: centre.x + point.x, y: centre.y + point.y };
		});
	const roundedPath = (points: readonly Point[], cornerRadius: number) =>
		roundedPolygonPath(points, cornerRadius);
	const currentPoints = pointsAt((spoke) => spoke.position);

	if (spokes.length === 0) {
		return (
			<div
				className={cn(
					"flex items-center justify-center text-caption text-muted-foreground",
					className,
				)}
				style={{ height: size }}
			>
				Nothing to plot yet
			</div>
		);
	}

	return (
		<svg
			viewBox={`0 0 ${size} ${size}`}
			className={cn("h-auto w-full max-w-full", className)}
			role="img"
			aria-label={title}
		>
			<title>{title}</title>

			{Array.from({ length: safeRings }, (_, i) => i + 1).map((ring) => (
				<path
					key={ring}
					d={roundedPath(
						pointsAt(() => ring),
						Math.min(10, ((ring / safeRings) * radius) / 8),
					)}
					fill="none"
					stroke={GRID}
					strokeWidth={1}
				/>
			))}

			{spokes.map((spoke, i) => {
				const label = vertex(
					i,
					spokes.length,
					safeRings,
					safeRings,
					radius + 18,
				);
				// Every label stays on the same radius. Labels nearest the horizontal
				// sides are centred over that point so their text grows inward and
				// outward evenly instead of being moved closer to the grid.
				const nearHorizontal = Math.abs(label.x) > Math.abs(label.y) * 1.5;
				const anchor =
					nearHorizontal || Math.abs(label.x) < radius * 0.22
						? "middle"
						: label.x > 0
							? "start"
							: "end";
				const lines = radarLabelLines(spoke.label);
				const lineHeight = 13;
				const x = centre.x + label.x;
				const y = centre.y + label.y - ((lines.length - 1) * lineHeight) / 2;
				return (
					<g key={spoke.key}>
						<text
							x={x}
							y={y}
							textAnchor={anchor}
							dominantBaseline="middle"
							fontSize={11}
							fontWeight={600}
							fill={LABEL}
						>
							{lines.map((line, lineIndex) => (
								<tspan
									key={`${line}-${lineIndex}`}
									x={x}
									dy={lineIndex === 0 ? 0 : lineHeight}
								>
									{line}
								</tspan>
							))}
						</text>
					</g>
				);
			})}

			{hasBefore && (
				<path
					d={roundedPath(
						pointsAt((spoke) => spoke.before),
						8,
					)}
					fill="none"
					stroke={EARLIER}
					strokeWidth={1.5}
					strokeDasharray="5 4"
				/>
			)}

			{hasPotential && (
				<path
					d={roundedPath(
						pointsAt((spoke) => spoke.potential),
						10,
					)}
					fill={POTENTIAL_FILL}
					stroke={ORANGE}
					strokeWidth={2}
					strokeDasharray="6 5"
					strokeOpacity={0.65}
				/>
			)}

			<path
				d={roundedPath(currentPoints, 10)}
				fill={ORANGE_FILL}
				stroke={ORANGE}
				strokeWidth={2.5}
			/>

			{spokes.map((spoke, i) => {
				const point = roundedPolygonMarkerPoint(currentPoints, i, 10);
				return (
					<circle
						key={spoke.key}
						cx={point.x}
						cy={point.y}
						r={3.2}
						fill={ORANGE}
					/>
				);
			})}
		</svg>
	);
}
